import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { BACKEND_URL } from '../config/canonical';
import {
  Camera, User, Search, RefreshCw, ExternalLink, Download, FileText,
  Image, Film, Mic, File, Trash2, Eye
} from 'lucide-react';
import { toast } from '../hooks/use-toast';

const TYPE_ICONS = {
  PHOTO: Image, VIDEO: Film, AUDIO: Mic, DOCUMENT: FileText, PDF: FileText, OTHER: File
};

const Evidence = () => {
  const { user } = useAuth();
  const token = localStorage.getItem('nexus_token');
  const [cases, setCases] = useState([]);
  const [selectedCase, setSelectedCase] = useState('');
  const [evidence, setEvidence] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  const isAdmin = user?.role === 'department_admin' || user?.role === 'main_hub';

  const fetchCases = useCallback(async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/cases`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) setCases(await res.json());
    } catch (e) {
      console.error('Fetch cases error:', e);
    }
  }, [token]);

  const fetchEvidence = useCallback(async () => {
    if (!selectedCase) {
      setEvidence([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/cases/${selectedCase}/evidence`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) setEvidence(await res.json());
    } catch (e) {
      console.error('Fetch evidence error:', e);
    } finally {
      setLoading(false);
    }
  }, [token, selectedCase]);

  useEffect(() => { fetchCases(); }, [fetchCases]);
  useEffect(() => { fetchEvidence(); }, [fetchEvidence]);

  const handleDelete = async (evidenceId) => {
    if (!window.confirm('Delete this evidence?')) return;
    await fetch(`${BACKEND_URL}/api/evidence/${evidenceId}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } });
    fetchEvidence();
  };

  const filtered = evidence.filter(e => {
    if (!search) return true;
    const s = search.toLowerCase();
    return e.file_name?.toLowerCase().includes(s) || e.description?.toLowerCase().includes(s) || e.evidence_type?.toLowerCase().includes(s);
  });

  const formatSize = (bytes) => {
    if (!bytes) return 'N/A';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  return (
    <div className="space-y-6" data-testid="evidence-page">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <div className="nx-eyebrow">Field Operations</div>
          <h1 className="nx-h1 mt-1">Evidence Vault</h1>
          <p className="nx-body mt-1">Evidence files and chain of custody tracking</p>
        </div>
        {selectedCase && (
          <Link to={`/cases/${selectedCase}`}>
            <Button variant="outline" className="border-slate-300 text-slate-300">
              <ExternalLink className="w-4 h-4 mr-2" /> Upload from Case Detail
            </Button>
          </Link>
        )}
      </div>

      {/* Filters */}
      <Card className="bg-white border-slate-200">
        <CardContent className="p-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Select value={selectedCase} onValueChange={setSelectedCase}>
              <SelectTrigger className="bg-slate-50 border-slate-300 text-slate-900" data-testid="evidence-case-select">
                <SelectValue placeholder="Select a case..." />
              </SelectTrigger>
              <SelectContent>
                {cases.map(c => (
                  <SelectItem key={c.id} value={c.id}>{c.case_number} - {c.title}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
              <Input value={search} onChange={e => setSearch(e.target.value)}
                className="bg-slate-50 border-slate-300 text-slate-900 pl-10" placeholder="Search evidence..." />
            </div>
            <Button variant="outline" className="border-slate-300 text-slate-300" onClick={fetchEvidence}>
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} /> Refresh
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Content */}
      {!selectedCase ? (
        <Card className="bg-white border-slate-200">
          <CardContent className="p-12 text-center">
            <Camera className="w-16 h-16 mx-auto text-slate-600 mb-4" />
            <h3 className="text-xl font-semibold text-slate-800 mb-2">Select a Case</h3>
            <p className="text-slate-500">Choose a case to view its evidence files</p>
          </CardContent>
        </Card>
      ) : loading ? (
        <div className="text-center py-12 text-slate-400">Loading evidence...</div>
      ) : filtered.length === 0 ? (
        <Card className="bg-white border-slate-200">
          <CardContent className="p-12 text-center">
            <Camera className="w-16 h-16 mx-auto text-slate-600 mb-4" />
            <h3 className="text-xl font-semibold text-slate-800 mb-2">No Evidence Found</h3>
            <p className="text-slate-500">{search ? 'Try a different search' : 'No evidence uploaded for this case'}</p>
            <Link to={`/cases/${selectedCase}`}>
              <Button className="mt-4 bg-[#0F172A] hover:bg-[#1E293B] text-white">Upload from Case Detail</Button>
            </Link>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map(ev => {
            const TypeIcon = TYPE_ICONS[ev.evidence_type] || File;
            return (
              <Card key={ev.id} className="bg-white border-slate-200" data-testid={`evidence-card-${ev.id}`}>
                <CardContent className="p-4">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 bg-slate-700 rounded-lg flex items-center justify-center flex-shrink-0">
                      <TypeIcon className="w-5 h-5 text-amber-400" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          <p className="text-slate-900 text-sm font-semibold truncate">{ev.file_name}</p>
                          <Badge variant="outline" className="border-slate-300 text-slate-600 text-xs mt-1">
                            {ev.evidence_type}
                          </Badge>
                        </div>
                        <div className="flex gap-1 flex-shrink-0">
                          <button
                            onClick={() => window.open(`${BACKEND_URL}/api/evidence/${ev.id}/view`, '_blank')}
                            className="text-slate-400 hover:text-sky-400 p-1"
                            data-testid={`ev-view-${ev.id}`}
                            title="Preview file"
                          >
                            <Eye className="w-4 h-4" />
                          </button>
                          <button
                            onClick={async () => {
                              try {
                                const res = await fetch(`${BACKEND_URL}/api/evidence/${ev.id}/download`, { headers: { Authorization: `Bearer ${token}` } });
                                if (!res.ok) {
                                  const err = await res.json().catch(() => ({}));
                                  throw new Error(err?.detail || `Download failed (${res.status})`);
                                }
                                const blob = await res.blob();
                                const url = URL.createObjectURL(blob);
                                const a = document.createElement('a');
                                a.href = url;
                                a.download = ev.file_name || `evidence-${ev.id}`;
                                document.body.appendChild(a);
                                a.click();
                                document.body.removeChild(a);
                                setTimeout(() => URL.revokeObjectURL(url), 1500);
                              } catch (e) {
                                toast({ title: 'Download failed', description: e.message, variant: 'destructive' });
                              }
                            }}
                            className="text-slate-400 hover:text-amber-400 p-1"
                            data-testid={`download-${ev.id}`}
                            title="Download"
                          >
                            <Download className="w-4 h-4" />
                          </button>
                          {isAdmin && (
                            <button onClick={() => handleDelete(ev.id)} className="text-slate-400 hover:text-red-400 p-1">
                              <Trash2 className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      </div>
                      {ev.description && <p className="text-slate-600 text-xs mt-1 line-clamp-2">{ev.description}</p>}
                      <div className="flex items-center justify-between mt-2">
                        <div className="flex items-center gap-1 text-xs text-slate-500">
                          <User className="w-3 h-3" /> {ev.uploaded_by_name}
                        </div>
                        <span className="text-xs text-slate-500">{formatSize(ev.file_size)}</span>
                      </div>
                      <p className="text-xs text-slate-500 mt-1">{new Date(ev.uploaded_at).toLocaleString()}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default Evidence;
